import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "../styles/Sandbox.css";

interface ThemeMap {
  [key: string]: string;
}

interface SoundMap {
  [key: string]: string;
}

interface Soundscape {
  url: string;
  name: string;
}

interface SoundscapeMap {
  [key: string]: Soundscape;
}

interface EjectResponse {
  success: boolean;
  message: string;
}

const themes: ThemeMap = {
  light: "/assets/themes/sandbox/SandboxLight.css",
  dark: "/assets/themes/sandbox/SandboxDark.css",
  retro: "/assets/themes/sandbox/SandboxRetro.css",
};

const sounds: SoundMap = {
  buttonClick: "/assets/themes/sandbox/sounds/click1.mp3",
  notification: "/assets/themes/sandbox/sounds/click2.mp3",
};

const soundscapes: SoundscapeMap = {
  walking: {
    url: "/assets/themes/sandbox/sounds/walking.mp3",
    name: "Walking Ambience",
  },
  ocean: {
    url: "/assets/themes/sandbox/sounds/ocean.mp3",
    name: "Ocean Waves",
  },
};

interface FileContentResponse {
  content: string | null;
}

const SandboxTest = () => {
  const [currentTheme, setCurrentTheme] = useState<string>("light");
  const [currentSoundscape, setCurrentSoundscape] = useState<string | null>(
    null,
  );
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(0.5);
  const [themeFileChecked, setThemeFileChecked] = useState<boolean>(false);
  const [ejecting, setEjecting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [storyText, setStoryText] = useState("");
  const [storySubmitting, setStorySubmitting] = useState(false);
  const [storySuccess, setStorySuccess] = useState<string | null>(null);
  const [storyError, setStoryError] = useState<string | null>(null);
  const [devices, setDevices] = useState<{ id: string; name: string }[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(true);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const buttonSoundRef = useRef<HTMLAudioElement | null>(null);
  const clickSoundRef = useRef<HTMLAudioElement | null>(null);

  const FastAPIUrl = process.env.FASTAPI_URL;

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
  });

  useEffect(() => {
    const fetchFile = async () => {
      const result = await getFileContent("theme.txt");

      if (result.content && Object.keys(themes).includes(result.content)) {
        setCurrentTheme(result.content);
        console.log(`Theme set to: ${result.content}`);
      } else {
        console.warn(`Invalid theme name in file: ${result}`);
      }

      setThemeFileChecked(true);
    };

    fetchFile();
  }, []);

  useEffect(() => {
    // Remove any existing theme stylesheet link
    const existingThemeLink = document.getElementById("theme-stylesheet");
    if (existingThemeLink) {
      existingThemeLink.remove();
    }

    // Create and append new theme stylesheet link
    const link = document.createElement("link");
    link.id = "theme-stylesheet";
    link.rel = "stylesheet";
    link.href = themes[currentTheme];
    document.head.appendChild(link);

    // Save theme preference to localStorage
    localStorage.setItem("preferred-theme", currentTheme);
  }, [currentTheme]);

  // Initialize theme from localStorage on first render
  useEffect(() => {
    const savedTheme = localStorage.getItem("preferred-theme");
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme);
    }
  }, []);

  // Clean up audio when component unmounts
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
    };
  }, []);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const response = await axios.get(`${FastAPIUrl}/device/playground`);
        setDevices(response.data); // assuming array of { id, name }
      } catch (err) {
        console.error("Failed to load devices:", err);
      } finally {
        setLoadingDevices(false);
      }
    };

    fetchDevices();
  }, []);

  const getFileContent = async (
    filepath: string,
  ): Promise<FileContentResponse> => {
    try {
      const response = await fetch(
        `${FastAPIUrl}/playground?filepath=${encodeURIComponent(filepath)}`,
      );
      if (!response.ok) throw new Error("Failed to fetch file content");
      return (await response.json()) as FileContentResponse;
    } catch (error) {
      console.error("Error fetching file content:", error);
      return { content: null };
    }
  };

  const ejectFloppy = async (): Promise<EjectResponse> => {
    try {
      const response = await fetch(`${FastAPIUrl}/eject-floppy`, {
        method: "POST",
      });

      const data = (await response.json()) as EjectResponse;

      if (!response.ok) {
        throw new Error(data.message || "Failed to eject floppy");
      }

      return data;
    } catch (error) {
      console.error("Error ejecting floppy:", error);
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  };

  // Function to play button click sound
  const playButtonSound = (): void => {
    if (buttonSoundRef.current) {
      buttonSoundRef.current.currentTime = 0;
      buttonSoundRef.current
        .play()
        .catch((err) => console.error("Error playing sound:", err));
    }
  };

  const playClickSound = (): void => {
    if (clickSoundRef.current) {
      clickSoundRef.current.currentTime = 0;
      clickSoundRef.current
        .play()
        .catch((err) => console.error("Error playing sound:", err));
    }
  };

  // Function to toggle soundscape
  const toggleSoundscape = (soundscapeName: string): void => {
    if (currentSoundscape === soundscapeName && isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setIsPlaying(false);
    } else {
      setCurrentSoundscape(soundscapeName);
      if (audioRef.current) {
        audioRef.current.src = soundscapes[soundscapeName].url;
        audioRef.current.loop = true;
        audioRef.current.volume = volume;
        audioRef.current
          .play()
          .catch((err) => console.error("Error playing soundscape:", err));
        setIsPlaying(true);
      }
    }
  };

  // Handle volume change
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleEject = async () => {
    setEjecting(true);
    setMessage(null);

    try {
      const result = await ejectFloppy();
      setMessage(result.message);
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Error ejecting floppy",
      );
    } finally {
      setEjecting(false);
    }
  };

  const handleSubmitStory = async (e: React.FormEvent) => {
    e.preventDefault();
    setStorySubmitting(true);
    setStorySuccess(null);
    setStoryError(null);

    try {
      const response = await axios.post(`${FastAPIUrl}/playground/story`, {
        deviceId: selectedDevice,
        authorName,
        storyText,
      });

      setStorySuccess("User story submitted successfully.");
      setSelectedDevice("");
      setAuthorName("");
      setStoryText("");
    } catch (err) {
      setStoryError("Failed to submit the story. Please try again.");
    } finally {
      setStorySubmitting(false);
    }
  };

  return (
    <div className="sandbox-container">
      <h1>Sandbox</h1>

      {/* Theme Section */}
      <section className="sandbox-section">
        <h2>Theme Testing</h2>

        {themeFileChecked !== null ? (
          <pre>{currentTheme}</pre>
        ) : (
          <p>Loading...</p>
        )}

        <div className="theme-buttons">
          {Object.keys(themes).map((theme) => (
            <button
              key={theme}
              className={`theme-button ${currentTheme === theme ? "active" : ""}`}
              onClick={() => {
                setCurrentTheme(theme);
                playButtonSound();
              }}
            >
              {theme.charAt(0).toUpperCase() + theme.slice(1)} Theme
            </button>
          ))}
        </div>
      </section>

      {/* Soundscapes Section */}
      <section className="sandbox-section">
        <h2>Soundscapes</h2>
        <div className="soundscape-controls">
          {Object.keys(soundscapes).map((soundscape) => (
            <button
              key={soundscape}
              className={`soundscape-button ${currentSoundscape === soundscape && isPlaying ? "playing" : ""}`}
              onClick={() => {
                toggleSoundscape(soundscape);
                playClickSound();
              }}
            >
              {currentSoundscape === soundscape && isPlaying ? "Stop" : "Play"}{" "}
              {soundscapes[soundscape].name}
            </button>
          ))}

          <div className="volume-control">
            <label htmlFor="volume">Volume: {Math.round(volume * 100)}%</label>
            <input
              type="range"
              id="volume"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolumeChange}
            />
          </div>
        </div>
      </section>

      {/* Button Sound Test */}
      <section className="sandbox-section">
        <h2>Sound Effect Testing</h2>
        <button
          className="sound-test-button"
          onClick={() => {
            playButtonSound();
            console.log("Button clicked with sound effect");
          }}
        >
          Click Me For Sound
        </button>
      </section>

      {/* Audio elements */}
      <audio ref={audioRef} />
      <audio ref={buttonSoundRef} src={sounds.buttonClick} preload="auto" />
      <audio ref={clickSoundRef} src={sounds.notification} preload="auto" />

      {/* Theme display */}
      <div className="theme-display">
        <div className="theme-indicator">
          <span>Current Theme: </span>
          <strong>
            {currentTheme.charAt(0).toUpperCase() + currentTheme.slice(1)}
          </strong>
        </div>
        <div className="theme-example">
          <p>
            This is sample text that will change appearance based on the theme.
          </p>
          <button className="theme-sample-button">Sample Button</button>
          <input type="text" placeholder="Sample input field" />
        </div>
      </div>

      {/* User Story Submission Form */}
      <section className="sandbox-section">
        <h2>Add User Story (Playground)</h2>
        <form onSubmit={handleSubmitStory} className="user-story-form">
          <div>
            <label>Select Device:</label>
            {loadingDevices ? (
              <p>Loading devices...</p>
            ) : (
              <select
                value={selectedDevice}
                onChange={(e) => setSelectedDevice(e.target.value)}
                required
              >
                <option value="">-- Choose Device --</option>
                {devices.map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label>Your Name:</label>
            <input
              type="text"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              required
            />
          </div>

          <div>
            <label>Your Story:</label>
            <textarea
              value={storyText}
              onChange={(e) => setStoryText(e.target.value)}
              rows={4}
              required
            />
          </div>

          <button type="submit" disabled={storySubmitting}>
            {storySubmitting ? "Submitting..." : "Submit Story"}
          </button>

          {storySuccess && <p className="success-message">{storySuccess}</p>}
          {storyError && <p className="error-message">{storyError}</p>}
        </form>
      </section>

      <div className="floppy-control">
        <h3>Floppy Disk Control</h3>
        <button onClick={handleEject} disabled={ejecting}>
          {ejecting ? "Ejecting..." : "Eject Floppy"}
        </button>

        {message && (
          <div className={message.includes("success") ? "success" : "error"}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default SandboxTest;
